import React, { useCallback, useState } from 'react';
import { Icon, Spinner, fetchJson } from '@heartinternet/hi-ui';
import type { Service } from '../types';
import { getPrimaryDomain } from '../utils';
import { DropdownMenu } from '../../../components/DropdownMenu';

export interface PackageManageMenuProps {
  row: Service;
  isWordpress: boolean;
  apiPath: string;
  csrfToken: string;
  upgradeEnabled: boolean;
  onError: (error: string) => void;
  onAssign: (serviceId: string) => void;
  onUpgrade: (service: Service) => void;
}

/**
 * Row-actions "Manage" cog dropdown for a hosting package (Managed WordPress / cPanel).
 * Open/close, hover-bridge and on-screen positioning come from the shared DropdownMenu
 * component; this component supplies the package-table markup, the per-row SSO loading
 * state, and the menu items (Websites/Mailboxes vs Assign, plus Upgrade).
 */
export const PackageManageMenu: React.FC<PackageManageMenuProps> = ({
  row,
  isWordpress,
  apiPath,
  csrfToken,
  upgradeEnabled,
  onError,
  onAssign,
  onUpgrade,
}) => {
  const [ssoLoading, setSsoLoading] = useState(false);
  const primary = getPrimaryDomain(row);

  const handleSSOClick = useCallback(
    async (close: () => void) => {
      close();
      setSsoLoading(true);
      let newWindow: globalThis.Window | null = null;
      try {
        newWindow = window.open('/redirect.html', '_blank');
        if (newWindow) {
          newWindow.document.body.innerHTML =
            '<div style="font-family:sans-serif;text-align:center;padding:60px">' +
            '<h2>Please wait...</h2>' +
            `<p>You're being redirected to the ${isWordpress ? 'WordPress' : 'cPanel'} Manage page</p>` +
            '<p>This may take up to 10 seconds.</p></div>';
        }
      } catch {
        // Popup may be blocked or cross-origin restricted
      }

      const ssoRes = await fetchJson<{ sso_url: string }>(`${apiPath}/${row.id}/sso`, {
        headers: { 'X-CSRF-Token': csrfToken },
      });

      setSsoLoading(false);

      if (ssoRes.error) {
        if (newWindow) newWindow.close();
        onError(ssoRes.error);
      } else if (ssoRes.data?.sso_url) {
        if (newWindow) {
          newWindow.location.href = ssoRes.data.sso_url;
        } else {
          window.location.href = ssoRes.data.sso_url;
        }
      }
    },
    [apiPath, csrfToken, isWordpress, onError, row.id]
  );

  const wrapperClassName = `hi-ui-package-table__manage-dropdown${
    row.is_suspended ? ' hi-ui-package-table__manage-dropdown--disabled' : ''
  }`;

  return (
    <DropdownMenu
      openOn="hover"
      disabled={row.is_suspended || ssoLoading}
      className={wrapperClassName}
      openClassName="hi-ui-package-table__manage-dropdown--open"
      menuClassName="hi-ui-package-table__manage-menu"
      renderTrigger={({ ref, toggle }) => (
        <button
          ref={ref}
          className={`hi-ui-package-table__cog-trigger${
            ssoLoading ? ' hi-ui-package-table__cog-trigger--loading' : ''
          }`}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          disabled={row.is_suspended}
          aria-label={ssoLoading ? 'Loading...' : 'Package options'}
        >
          {ssoLoading ? <Spinner size="small" /> : <Icon name="cog" size={20} />}
        </button>
      )}
    >
      {({ close }) => (
        <>
          {primary ? (
            <>
              <button
                className="hi-ui-package-table__manage-item"
                onClick={() => handleSSOClick(close)}
              >
                Websites
              </button>
              <a
                className="hi-ui-package-table__manage-item"
                href={`/cp/businessemail/list?service=${row.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Mailboxes
              </a>
            </>
          ) : (
            <button
              className="hi-ui-package-table__manage-item"
              onClick={() => {
                close();
                onAssign(row.id);
              }}
            >
              Assign
            </button>
          )}
          {upgradeEnabled && row.is_upgradable && (
            <button
              className="hi-ui-package-table__manage-item"
              onClick={() => {
                close();
                onUpgrade(row);
              }}
            >
              Upgrade
            </button>
          )}
        </>
      )}
    </DropdownMenu>
  );
};
